import type { ReactNode } from "react";

/**
 * The teaching layer for each analysis.
 *
 * A ranked table is useless to someone who does not know why the ranking is
 * meaningful. Each entry states the question, names the naive metric people
 * reach for, explains what that metric is confounded with, then shows the
 * patterns worth acting on.
 */

export interface Pattern {
  signal: string;
  means: string;
  action: string;
}

export interface Explainer {
  question: string;
  naive: string;
  problem: string;
  example: ReactNode;
  visual: ReactNode;
  patterns: Pattern[];
}

const V = "#5b21b6", DIM = "#c4b5fd", AMBER = "#f59e0b", INK = "#8b869e";
const mono = "JetBrains Mono, monospace";

export const EXPLAINERS: Record<string, Explainer> = {

  coverage: {
    question: "How much of this property's search data can you actually see?",
    naive: "Reading query reports as though they cover everything.",
    problem:
      "Google withholds the query on impressions where the term was rare enough to identify a person. Those impressions and clicks still count in your totals, but they carry no query. On a small property this can be most of them, so every query-level number you compute is drawn from a minority of the data.",
    example: (
      <>
        A property gets 10,000 impressions. 4,000 have the query withheld. Your
        query table sums to 6,000 impressions and looks complete, because
        nothing tells you the other 4,000 existed. Any statement of the form
        "our top 50 queries drive most of our traffic" is measured against the
        wrong denominator.
      </>
    ),
    visual: (
      <svg viewBox="0 0 320 74" width="100%" role="img"
           aria-label="Visible versus withheld impressions">
        <rect x="0" y="14" width="186" height="26" rx="3" fill={V} />
        <rect x="188" y="14" width="124" height="26" rx="3" fill={DIM} />
        <text x="8" y="31" fontSize="10" fill="#fff" fontFamily={mono}>visible queries</text>
        <text x="196" y="31" fontSize="10" fill={V} fontFamily={mono}>withheld</text>
        <text x="0" y="56" fontSize="9" fill={INK} fontFamily={mono}>
          both are real traffic; only one has a query attached
        </text>
      </svg>
    ),
    patterns: [
      { signal: "Withheld share above 40%",
        means: "Most of your search demand is long-tail terms too rare to name.",
        action: "Report page-level findings rather than query-level ones, and caveat any query analysis with this number." },
      { signal: "Withheld share rising over time",
        means: "You are gaining visibility on more rare terms, which is usually growth.",
        action: "Track total impressions alongside it; rising withheld share with rising totals is healthy." },
      { signal: "Withheld click share far below withheld impression share",
        means: "Rare terms are showing but not converting to clicks.",
        action: "Normal. Long-tail impressions often sit at low positions." },
    ],
  },

  ctr_residual: {
    question: "Where has this site earned its position but not the click?",
    naive: "Sorting queries by CTR and working on the lowest.",
    problem:
      "CTR is mostly a restatement of position. A term at position 2 with 18% CTR and one at position 9 with 3% are not comparable, so ranking by CTR just re-ranks by position and tells you nothing new. Fitting an expected CTR from this property's own results at each position, device and SERP appearance removes position from the measurement. What remains is the part you control: whether the title and snippet match the intent.",
    example: (
      <>
        Two queries both sit at position 4. One earns 9% CTR, the other 3%. This
        property's own terms at position 4 average 8%. The first is fine; the
        second is losing roughly 5 clicks per 100 impressions to something other
        than rank. At 4,000 impressions that is 200 clicks a month, which is the
        number the column reports.
      </>
    ),
    visual: (
      <svg viewBox="0 0 320 96" width="100%" role="img"
           aria-label="Actual CTR against the fitted curve">
        <path d="M14,20 C60,34 110,56 200,70 L306,80" fill="none"
              stroke={DIM} strokeWidth="2" />
        <text x="212" y="94" fontSize="9" fill={INK} fontFamily={mono}>expected at position</text>
        <circle cx="90" cy="46" r="4" fill={V} />
        <text x="98" y="44" fontSize="9" fill={V} fontFamily={mono}>above curve</text>
        <circle cx="150" cy="82" r="4" fill={AMBER} />
        <line x1="150" y1="82" x2="150" y2="60" stroke={AMBER} strokeDasharray="2 2" />
        <text x="158" y="86" fontSize="9" fill={AMBER} fontFamily={mono}>the gap is the opportunity</text>
      </svg>
    ),
    patterns: [
      { signal: "Large clicks forgone on a brand term",
        means: "Something is intercepting the click: a sitelink, an aggregator, or a competitor bidding.",
        action: "Check the live SERP before changing anything on the page." },
      { signal: "Whole clusters of pages below the curve",
        means: "A template-level title or meta description problem, not a page one.",
        action: "Fix the template. One change, many pages." },
      { signal: "Consistently above the curve at poor positions",
        means: "The snippet is doing unusually well for where it ranks.",
        action: "This is a ranking opportunity, not a snippet one. Position is the constraint." },
    ],
  },

  query_cohorts: {
    question: "Is the market changing, or are you?",
    naive: "Watching total impressions and assuming a fall means lost rankings.",
    problem:
      "Traffic falling because search demand shrank and traffic falling because you lost ground look identical in a line chart. Splitting each month's query set into terms that are new, retained from last month, or recovered after a gap separates those two stories.",
    example: (
      <>
        Impressions drop 20% month over month. If retained queries held steady
        and the drop is entirely in new queries, you did not lose rankings; the
        market simply threw off fewer novel searches. If retained queries fell
        while the query count held, you lost ground on terms you already had.
        Opposite diagnosis, opposite response.
      </>
    ),
    visual: (
      <svg viewBox="0 0 320 96" width="100%" role="img"
           aria-label="Query cohorts by month">
        {[0, 1, 2, 3, 4].map((i) => {
          const x = 16 + i * 60;
          const ret = 30 + i * 4, nw = 20 - i * 2, rec = 8;
          return (
            <g key={i}>
              <rect x={x} y={76 - ret} width="34" height={ret} fill={DIM} />
              <rect x={x} y={76 - ret - nw} width="34" height={nw} fill={V} />
              <rect x={x} y={76 - ret - nw - rec} width="34" height={rec} fill={AMBER} />
            </g>
          );
        })}
        <text x="16" y="92" fontSize="9" fill={INK} fontFamily={mono}>
          retained · new · recovered
        </text>
      </svg>
    ),
    patterns: [
      { signal: "New cohort growing, retained flat",
        means: "You are being found for terms you did not previously appear for.",
        action: "Look at what those terms have in common. It is often a content type you underrate." },
      { signal: "Retained cohort shrinking",
        means: "Genuine loss on terms you already held. This is the one that matters.",
        action: "Compare those terms period over period on the same query set." },
      { signal: "Recovered cohort spiking",
        means: "Terms returning after an absence, often seasonal or an indexing recovery.",
        action: "Check whether the timing matches a known event before crediting recent work." },
    ],
  },

  url_churn: {
    question: "Does Google know which of your pages answers this query?",
    naive: "Calling it cannibalization whenever two pages rank for the same term.",
    problem:
      "Two pages ranking simultaneously is normal and usually harmless. The real pathology is Google being unable to decide, which shows up as the winning URL flipping week to week. Each switch resets whatever authority the previous page had accumulated. You can only see this with daily query-by-URL rows, which the Search Console interface does not give you.",
    example: (
      <>
        A query alternates between your guide and your category page across six
        weeks: guide, category, guide, guide, category, category. Neither page
        holds position long enough to consolidate, and the term underperforms
        both. Nothing in the Search Console interface shows this, because it
        averages the weeks together.
      </>
    ),
    visual: (
      <svg viewBox="0 0 320 84" width="100%" role="img"
           aria-label="Winning URL by week">
        {["a","b","a","a","b","b","a"].map((w, i) => (
          <g key={i}>
            <rect x={14 + i * 42} y={w === "a" ? 16 : 44} width="32" height="20"
                  rx="3" fill={w === "a" ? V : AMBER} />
            <text x={14 + i * 42 + 16} y={w === "a" ? 30 : 58} fontSize="9"
                  textAnchor="middle" fill="#fff" fontFamily={mono}>
              {w === "a" ? "/guide" : "/cat"}
            </text>
          </g>
        ))}
        <text x="14" y="80" fontSize="9" fill={INK} fontFamily={mono}>
          six switches in seven weeks: Google cannot choose
        </text>
      </svg>
    ),
    patterns: [
      { signal: "Churn near 1.0 on a high-impression term",
        means: "Two pages are too similar for Google to distinguish, on a term that matters.",
        action: "Consolidate, or differentiate them sharply. Expand one row to see which pages are competing." },
      { signal: "Churn between a page and its paginated or filtered variant",
        means: "A canonical or faceted-navigation problem, not a content one.",
        action: "Fix canonicalization rather than rewriting anything." },
      { signal: "Churn that starts on a specific week",
        means: "Something shipped. A new page, a template change, a redirect.",
        action: "Check what changed that week before assuming it is a content overlap." },
    ],
  },

  mix_vs_rate: {
    question: "Did clicks change because you were shown more, or because more people clicked?",
    naive: "Reporting that clicks are up or down and stopping there.",
    problem:
      "A click change always has two components: how many times you were shown, and what share of those turned into clicks. They have completely different causes and completely different fixes. Holding one constant while the other moves separates them arithmetically.",
    example: (
      <>
        A page goes from 500 to 400 clicks. Impressions fell from 10,000 to
        6,000 while CTR rose from 5% to 6.7%. Volume cost you 200 clicks; rate
        gave back 100. So the page did not get worse at converting impressions,
        it got shown less. Working on the title would be the wrong response.
      </>
    ),
    visual: (
      <svg viewBox="0 0 320 90" width="100%" role="img"
           aria-label="Change split into volume and rate">
        <rect x="14" y="20" width="52" height="46" fill={DIM} />
        <text x="40" y="80" fontSize="9" textAnchor="middle" fill={INK} fontFamily={mono}>before</text>
        <rect x="96" y="40" width="52" height="26" fill={AMBER} />
        <text x="122" y="80" fontSize="9" textAnchor="middle" fill={AMBER} fontFamily={mono}>volume</text>
        <rect x="178" y="30" width="52" height="12" fill={V} />
        <text x="204" y="80" fontSize="9" textAnchor="middle" fill={V} fontFamily={mono}>rate</text>
        <rect x="258" y="30" width="52" height="36" fill={V} opacity=".55" />
        <text x="284" y="80" fontSize="9" textAnchor="middle" fill={INK} fontFamily={mono}>after</text>
      </svg>
    ),
    patterns: [
      { signal: "Loss almost entirely from volume",
        means: "Demand or visibility, not relevance. Rankings, indexing, or seasonality.",
        action: "Check position and impressions before touching the page." },
      { signal: "Loss almost entirely from rate",
        means: "The SERP around you changed, or your snippet did.",
        action: "Look at the live SERP. An AI Overview or a new feature above you is the usual cause." },
      { signal: "Both moving in opposite directions",
        means: "Mix shift. You are being shown for a different set of terms than before.",
        action: "Filter to this page and compare its query set across the two periods." },
    ],
  },

  position_volatility: {
    question: "Is this ranking stable, or does it just average out to that?",
    naive: "Reading average position as though it were the position.",
    problem:
      "A query averaging 6 with a tight spread and one averaging 6 that swings between 2 and 14 are completely different assets, and the average hides which you have. The gap between a typical day and a good day is usually winnable without new links, because the page has already proven it can rank there.",
    example: (
      <>
        Two terms both average position 6. The first sits at 5 to 7 every day.
        The second reaches 2 on its best days and 12 on its worst. The second
        has already demonstrated it can hold position 2, so the work is
        stability rather than authority. The average makes them look identical.
      </>
    ),
    visual: (
      <svg viewBox="0 0 320 88" width="100%" role="img"
           aria-label="Two distributions with the same average">
        <line x1="14" y1="26" x2="306" y2="26" stroke="#eeecf4" />
        <rect x="150" y="18" width="40" height="16" rx="3" fill={V} />
        <circle cx="170" cy="26" r="3" fill="#fff" />
        <text x="200" y="30" fontSize="9" fill={INK} fontFamily={mono}>tight: nothing to win</text>
        <line x1="14" y1="62" x2="306" y2="62" stroke="#eeecf4" />
        <rect x="60" y="54" width="180" height="16" rx="3" fill={AMBER} opacity=".55" />
        <circle cx="170" cy="62" r="3" fill="#fff" stroke={AMBER} strokeWidth="2" />
        <text x="60" y="84" fontSize="9" fill={AMBER} fontFamily={mono}>
          same average, but the best days prove the ceiling
        </text>
      </svg>
    ),
    patterns: [
      { signal: "Large ceiling gap on a high-impression term",
        means: "The page can already rank higher than it typically does.",
        action: "Stability work: internal links, freshness, technical consistency. Not new content." },
      { signal: "Wide spread with no good days",
        means: "Genuinely unstable rather than underperforming.",
        action: "Check for URL churn on the same term. The two often go together." },
      { signal: "Tight spread at a poor position",
        means: "A settled ranking. Nothing incremental to recover here.",
        action: "This needs a step change in the page or its authority, not optimization." },
    ],
  },

  demand_capture: {
    question: "How much of the demand a page is shown for does it actually take?",
    naive: "Ranking pages by clicks, which just ranks them by how much they are shown.",
    problem:
      "Clicks reward pages that already get impressions. Capture rate asks a different question: of the demand this page was put in front of, how much did it convert. A page shown for many terms with low capture is often ranking for things it does not actually answer, which no amount of on-page work will fix.",
    example: (
      <>
        Two pages each take 500 clicks. One from 5,000 impressions across 40
        queries, the other from 50,000 across 900. The second looks like a
        winner on clicks and is a problem on capture: it is being shown for
        hundreds of terms it does not answer, which drags the whole page.
      </>
    ),
    visual: (
      <svg viewBox="0 0 320 90" width="100%" role="img" aria-label="Demand captured and forgone">
        <rect x="14" y="18" width="292" height="22" rx="3" fill={DIM} />
        <rect x="14" y="18" width="58" height="22" rx="3" fill={V} />
        <text x="20" y="33" fontSize="9" fill="#fff" fontFamily={mono}>taken</text>
        <text x="80" y="33" fontSize="9" fill={V} fontFamily={mono}>shown but not taken</text>
        <text x="14" y="60" fontSize="9" fill={INK} fontFamily={mono}>
          breadth without capture usually means ranking for the wrong intents
        </text>
      </svg>
    ),
    patterns: [
      { signal: "High query breadth, low capture",
        means: "The page ranks for intents it does not serve.",
        action: "Narrow the page's focus, or split it. Filter to this page and read its query list first." },
      { signal: "Low breadth, high capture",
        means: "A precise page doing its job.",
        action: "Look for adjacent terms it could also serve. This is where expansion pays." },
      { signal: "Low capture at a good average position",
        means: "Position is not the problem, so the snippet or the intent match is.",
        action: "Cross-check this page in CTR vs expected." },
    ],
  },
};
